import { create } from 'zustand';
import { apiFetch } from '../lib/api';

/**
 * Cache global dos Estabelecimentos do tenant logado
 * (REQ-005 / ADR-030).
 *
 * Antes deste store, cada tela que precisava da lista
 * (`SeletorEstabelecimento` em Dashboard/Inbox/CRM, `ModalCaixa`,
 * `SettingsEstabelecimentos`) fazia o próprio `apiFetch('/estabelecimentos')`
 * em `useEffect`. Navegar entre as 3 telas com seletor produzia 3
 * requests pro mesmo endpoint, e single-unit piscava porque cada
 * mount esperava o fetch.
 *
 * Padrão herdado de `modulos.store`/`branding.store`:
 *  - Cache compartilhado em Zustand (todas as telas leem o mesmo array).
 *  - `recarregar()` é idempotente: 1ª chamada faz fetch; chamadas
 *    seguintes só refazem se `carregado=false` OU `forcar=true`.
 *  - Fetch SILENCIOSO em refetches — só seta `carregando: true` no
 *    primeiro load. Refetches mantêm a lista atual visível até a
 *    resposta nova chegar.
 *  - Erro em refetch mantém estado atual (não esvazia).
 *
 * `limpar()` no logout zera a flag — próximo login refaz o fetch.
 *
 * Invalidação após mutação (CRUD em `SettingsEstabelecimentos`):
 * chamar `useEstabelecimentosStore.getState().recarregar(true)`.
 */

export interface Estabelecimento {
  id: number;
  nome: string;
  ativo: boolean;
}

interface EstabelecimentosState {
  estabelecimentos: Estabelecimento[];
  carregado: boolean;
  carregando: boolean;
  /**
   * Carrega a lista. `forcar=false` (default): se já carregado, no-op
   * (cache hit). `forcar=true`: ignora cache e refaz o fetch — usado
   * pelo `SettingsEstabelecimentos` após criar/editar/desativar pra
   * invalidar o cache compartilhado.
   */
  recarregar: (forcar?: boolean) => Promise<void>;
  limpar: () => void;
}

export const useEstabelecimentosStore = create<EstabelecimentosState>(
  (set, get) => ({
    estabelecimentos: [],
    carregado: false,
    carregando: false,

    recarregar: async (forcar = false) => {
      const { carregado } = get();
      if (carregado && !forcar) return;
      const primeiraVez = !carregado;
      if (primeiraVez) set({ carregando: true });
      try {
        const res = await apiFetch('/estabelecimentos');
        if (!res.ok) {
          if (primeiraVez) set({ carregando: false });
          return;
        }
        const lista = (await res.json()) as Estabelecimento[];
        set({
          estabelecimentos: lista,
          carregado: true,
          carregando: false,
        });
      } catch {
        if (primeiraVez) set({ carregando: false });
      }
    },

    limpar: () =>
      set({ estabelecimentos: [], carregado: false, carregando: false }),
  }),
);
